import { useState, useRef, useCallback, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useMagnum } from '@/context/MagnumContext'
import { apiFetch } from '@/lib/apiFetch'
import { useExerciseSession } from '@/hooks/useExerciseSession'
import { fetchMagnumMoment } from '@/lib/magnumMoment'
import { cn, wordSizeClass } from '@/lib/utils'

type GameState = 'idle' | 'loading' | 'playing' | 'done'
type TileState = 'default' | 'selected' | 'matched' | 'wrong'

interface Word {
  id: number
  text: string
  gradeLevel: number
}

interface SynonymPairsData {
  pairs: { word1: Word; word2: Word }[]
  allWords: Word[]
}

export default function SynonymPairsPage() {
  const { user } = useAuth()
  const magnum = useMagnum()
  const { sessionId, stats, activeMs, isIdle, startSession, endSession, recordAttempt, dismissIdle } = useExerciseSession({ exerciseType: 'SYNONYM_PAIRS' })

  const [state, setState] = useState<GameState>('idle')
  const [allWords, setAllWords] = useState<Word[]>([])
  const [tileStates, setTileStates] = useState<Record<number, TileState>>({})
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [matchedCount, setMatchedCount] = useState(0)
  const [totalPairs, setTotalPairs] = useState(0)
  const [showNewRecord, setShowNewRecord] = useState(false)
  const pairLookupRef = useRef<Set<string>>(new Set())
  const firstClickTimeRef = useRef(0)
  const clickSequenceRef = useRef<{ wordId: number; timestamp: number }[]>([])

  useEffect(() => {
    magnum.setFrameIdle(isIdle)
    if (isIdle) {
      fetchMagnumMoment({ contextType: 'IDLE_RETURN', exerciseType: 'SYNONYM_PAIRS',
      category: 'LANGUAGE', streak: stats.streak, sessionId, activeMs })
        .then((m) => magnum.handleEngineResponse(m))
        .catch(() => magnum.say("Still here. Still waiting. Still adorable."))
    }
  }, [isIdle])

  const buildPairKey = (id1: number, id2: number) => {
    const [a, b] = id1 < id2 ? [id1, id2] : [id2, id1]
    return `${a}-${b}`
  }

  const startGame = async () => {
    setState('loading')
    try {
      const [data] = await Promise.all([
        apiFetch<SynonymPairsData>('/exercises/synonym-pairs?pairs=10'),
        startSession(),
      ])
      const lookup = new Set<string>()
      data.pairs.forEach((p) => lookup.add(buildPairKey(p.word1.id, p.word2.id)))
      pairLookupRef.current = lookup

      setAllWords(data.allWords)
      setTileStates({})
      setSelectedId(null)
      setMatchedCount(0)
      setTotalPairs(data.pairs.length)
      setState('playing')

      fetchMagnumMoment({ contextType: 'GAME_STARTED', exerciseType: 'SYNONYM_PAIRS',
      category: 'LANGUAGE', streak: 0, sessionId })
        .then((m) => magnum.handleEngineResponse(m, 'happy'))
        .catch(() => {})
    } catch {
      setState('idle')
    }
  }

  const handleTileClick = useCallback((word: Word) => {
    if (state !== 'playing') return
    if (tileStates[word.id] === 'matched') return

    if (selectedId === null) {
      setSelectedId(word.id)
      setTileStates((prev) => ({ ...prev, [word.id]: 'selected' }))
      firstClickTimeRef.current = Date.now()
      clickSequenceRef.current = [{ wordId: word.id, timestamp: Date.now() }]
      return
    }

    if (selectedId === word.id) {
      setSelectedId(null)
      setTileStates((prev) => ({ ...prev, [word.id]: 'default' }))
      return
    }

    const responseMs = Date.now() - firstClickTimeRef.current
    clickSequenceRef.current.push({ wordId: word.id, timestamp: Date.now() })
    const isMatch = pairLookupRef.current.has(buildPairKey(selectedId, word.id))

    if (isMatch) {
      const prevSelectedId = selectedId
      setTileStates((prev) => ({
        ...prev,
        [prevSelectedId]: 'matched',
        [word.id]: 'matched',
      }))
      setSelectedId(null)

      const newMatched = matchedCount + 1
      setMatchedCount(newMatched)

      recordAttempt(word.id, true, responseMs, {
        word1Id: prevSelectedId,
        word2Id: word.id,
        clickSequence: clickSequenceRef.current,
      })

      const newStreak = stats.streak + 1
      const contextType = [3, 5, 7, 10].includes(newStreak) ? 'STREAK_MILESTONE' : 'CORRECT_ANSWER'
      fetchMagnumMoment({ contextType, exerciseType: 'SYNONYM_PAIRS',
      category: 'LANGUAGE', wordId: word.id, wordText: word.text, correct: true, responseMs, activeMs, streak: newStreak, sessionId })
        .then((m) => {
          magnum.handleEngineResponse(m, 'happy')
          if (m.timing?.newRecords?.length) {
            setShowNewRecord(true)
            setTimeout(() => setShowNewRecord(false), 3000)
          }
        })
        .catch(() => {})

      if (newMatched >= totalPairs) {
        setState('done')
        fetchMagnumMoment({ contextType: 'SESSION_END', exerciseType: 'SYNONYM_PAIRS',
      category: 'LANGUAGE', streak: newStreak, sessionId, activeMs })
          .then((m) => magnum.handleEngineResponse(m, 'excited'))
          .catch(() => {})
        endSession()
      }
    } else {
      const prevSelectedId = selectedId
      setTileStates((prev) => ({
        ...prev,
        [prevSelectedId]: 'wrong',
        [word.id]: 'wrong',
      }))
      setSelectedId(null)

      recordAttempt(word.id, false, responseMs, {
        word1Id: prevSelectedId,
        word2Id: word.id,
        clickSequence: clickSequenceRef.current,
      })

      fetchMagnumMoment({ contextType: 'WRONG_ANSWER', exerciseType: 'SYNONYM_PAIRS',
      category: 'LANGUAGE', wordId: word.id, wordText: word.text, correct: false, responseMs, activeMs, streak: 0, sessionId })
        .then((m) => magnum.handleEngineResponse(m, 'encouraging'))
        .catch(() => {})

      setTimeout(() => {
        setTileStates((prev) => {
          const next = { ...prev }
          if (next[prevSelectedId] === 'wrong') next[prevSelectedId] = 'default'
          if (next[word.id] === 'wrong') next[word.id] = 'default'
          return next
        })
      }, 600)
    }
  }, [state, selectedId, tileStates, matchedCount, totalPairs, stats.streak, sessionId, recordAttempt, endSession, magnum])

  useEffect(() => {
    if (state === 'idle') {
      startGame()
    }
  }, [])

  if (!user || user.type !== 'child') return null

  if (state === 'loading') {
    return (
      <div className="py-16 text-center">
        <p className="text-lg text-muted-foreground">Loading pairs...</p>
      </div>
    )
  }

  if (state === 'done') {
    const accuracy = stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0
    return (
      <div className="py-8 text-center space-y-8">
        <div className="text-6xl">🎉</div>
        <h1 className="text-3xl font-bold">All Pairs Found!</h1>
        <div className="grid grid-cols-3 gap-4 max-w-sm mx-auto">
          <div className="p-4 border border-border rounded-lg">
            <p className="text-2xl font-bold text-primary">{stats.correct}</p>
            <p className="text-xs text-muted-foreground">Matches</p>
          </div>
          <div className="p-4 border border-border rounded-lg">
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-xs text-muted-foreground">Attempts</p>
          </div>
          <div className="p-4 border border-border rounded-lg">
            <p className="text-2xl font-bold text-primary">{accuracy}%</p>
            <p className="text-xs text-muted-foreground">Accuracy</p>
          </div>
        </div>
        <div className="flex gap-4 justify-center pt-4">
          <button
            onClick={startGame}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-bold hover:opacity-90 transition"
          >
            Play Again
          </button>
          <Link
            to="/practice"
            className="px-6 py-3 border border-border rounded-xl font-bold hover:bg-accent transition inline-flex items-center"
          >
            Back to Exercises
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Stats bar */}
      <div className="flex justify-between items-center text-sm text-muted-foreground">
        <span>{matchedCount} of {totalPairs} pairs found</span>
        <span className="text-primary font-medium">{stats.correct} matches</span>
        {stats.total > 0 && (
          <span>Avg: {(stats.avgResponseMs / 1000).toFixed(1)}s</span>
        )}
        {stats.streak >= 3 && (
          <span className="text-amber-500 font-medium">🔥 {stats.streak} streak!</span>
        )}
      </div>

      {showNewRecord && (
        <div className="text-center animate-bounce">
          <span className="inline-block px-4 py-2 bg-amber-100 border-2 border-amber-400 rounded-xl text-amber-800 font-bold text-sm">
            New Record!
          </span>
        </div>
      )}

      {/* Progress bar */}
      <div className="w-full bg-accent rounded-full h-2">
        <div
          className="bg-primary h-2 rounded-full transition-all duration-300"
          style={{ width: `${(matchedCount / totalPairs) * 100}%` }}
        />
      </div>

      {/* Tile grid */}
      <div className="grid grid-cols-4 sm:grid-cols-5 gap-3">
        {allWords.map((word) => {
          const ts = tileStates[word.id] || 'default'
          return (
            <button
              key={word.id}
              onClick={() => handleTileClick(word)}
              disabled={ts === 'matched'}
              className={cn(
                'p-2 sm:p-3 rounded-xl font-bold transition-all duration-200 min-h-[56px] border-2 break-words leading-tight hyphens-auto',
                wordSizeClass(word.text, 'tile'),
                ts === 'default' && 'border-border bg-card hover:border-primary hover:bg-accent',
                ts === 'selected' && 'border-blue-500 bg-blue-50 text-blue-800',
                ts === 'matched' && 'border-green-300 bg-green-50 text-green-400 opacity-60 cursor-default',
                ts === 'wrong' && 'border-red-500 bg-red-50 text-red-800 animate-pulse',
              )}
            >
              {word.text}
            </button>
          )
        })}
      </div>
    </div>
  )
}
